import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';

export interface Column<T> {
  /** Stable key. */
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Right-align numeric/mono columns. */
  align?: 'start' | 'end';
  className?: string;
}

/**
 * Dense, hairline-divided data table (plan §2.5.2). Row hover = canvas-bone fill.
 * Selectable rows drive the multi-select bulk bar (spec §9.3).
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectable,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const allSelected = selectable && rows.length > 0 && rows.every((r) => selectedIds?.has(rowKey(r)));
  return (
    <div className="overflow-x-auto rounded-md border-[0.5px] border-hairline bg-canvas-paper">
      <table className="w-full border-collapse text-start">
        <thead>
          <tr className="border-b-[0.5px] border-hairline">
            {selectable && (
              <th className="w-[44px] px-md py-sm">
                <input
                  type="checkbox"
                  aria-label={t('ui.selectAllRows')}
                  checked={allSelected}
                  onChange={(e) => onToggleSelectAll?.(e.target.checked)}
                  className="accent-accent-blue"
                />
              </th>
            )}
            {columns.map((c) => (
              <th
                key={c.id}
                className={cn(
                  'px-md py-sm text-label uppercase tracking-[0.1em] text-ink-mute',
                  c.align === 'end' ? 'text-end' : 'text-start',
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = rowKey(row);
            return (
              <tr
                key={id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b-[0.5px] border-hairline last:border-b-0 transition-colors duration-almanac',
                  onRowClick && 'cursor-pointer hover:bg-canvas-bone',
                  selectedIds?.has(id) && 'bg-canvas-sand',
                )}
              >
                {selectable && (
                  <td className="px-md py-sm" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={t('ui.selectRow', { id })}
                      checked={selectedIds?.has(id) ?? false}
                      onChange={() => onToggleSelect?.(id)}
                      className="accent-accent-blue"
                    />
                  </td>
                )}
                {columns.map((c) => (
                  <td
                    key={c.id}
                    className={cn(
                      'px-md py-sm text-small text-ink-graphite',
                      c.align === 'end' ? 'text-end' : 'text-start',
                      c.className,
                    )}
                  >
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
